/* eslint-disable  */
import React, { useEffect, useState } from "react";
import {
  Banknote,
  Users,
  UserCheck,
  UserX,
  MailCheck,
  MailX,
  Wallet,
  ShoppingCart,
  ExternalLink,
  TrendingUp,
  ChevronRight,
  MapPin,
  PieChart as PieIcon,
  Headset,
  ReceiptText,
} from "lucide-react";
import { PieChart, Pie, Cell, Sector, ResponsiveContainer } from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import CediSymbol from "../Components/CediSymbol";
import axiosInstance from "../services/Axios/axios";
import { dataActionCreators, productActionCreators, inventoryActionCreators, supportActionCreators } from "../services/Actions";
import "./AdminDashboard.css";
import DashboardTrend from "./Components/DashboardTrend";
import RenderAdminPage from "./RenderAdminPage";
import DashboardSkeleton from "./Components/DashboardSkeleton";

const PIE_COLORS = [
  { color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  { color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  { color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  { color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
];

const renderActiveLoanStatus = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  return (
    <g>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 4}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 8} outerRadius={outerRadius + 10}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <text x={cx} y={cy - 14} textAnchor="middle" fill="#1f2937" fontSize={22} fontWeight={800}>{value.toLocaleString()}</text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill="#6b7280" fontSize={12} fontWeight={600}>{payload.name}</text>
      <text x={cx} y={cy + 26} textAnchor="middle" fill={fill} fontSize={12} fontWeight={700}>{`${(percent * 100).toFixed(1)}%`}</text>
    </g>
  );
};

function AdminDashboard() {
  const dispatch = useDispatch();
  const {
    GetAppliedLoans,
    ResetDataResponse,
    GetTotal,
    GetTotalTotalActiveNurse,
    GetTotalInactiveNurse,
    GetTotalVerifiedNurse,
    GetTotalUnverifiedNurse,
    GetTotalLoanStats,
    GetLoansForStatistics,
  } = bindActionCreators(dataActionCreators, dispatch);
  const { GetTotals } = bindActionCreators(productActionCreators, dispatch);
  const { GetInventoryItems, GetLowStock } = bindActionCreators(inventoryActionCreators, dispatch);
  const { GetTicketStats } = bindActionCreators(supportActionCreators, dispatch);
  const inventory = useSelector((state) => state?.inventory);
  const inventoryItemsCount = inventory?.items?.length || 0;
  const inventoryLowCount = inventory?.low_stock?.length || 0;
  const support = useSelector((state) => state?.support);
  const openTickets = support?.stats?.open || 0;
  const breachedTickets = support?.stats?.breached || 0;
  const data = useSelector((state) => state?.data);
  const {
    isLoading,
    response,
    totalMembers,
    totalActiveMembers,
    totalInactiveMembers,
    totalVerifiedMembers,
    totalUnverifiedMembers,
    totalFunds,
    totalLoanStats,
    loansStatistics,
  } = data;
  const product = useSelector((state) => state.product);
  const { totals } = product;
  const { totalOrders, totalSubmitedOrders } = totals;

  const [formData, setFormData] = useState({ status: "submitted", year: "" });
  const [regionStats, setRegionStats] = useState({});
  const [regionLoading, setRegionLoading] = useState(true);
  const [pieActiveIndex, setPieActiveIndex] = useState(0);
  const [initializing, setInitializing] = useState(true);

  const pieData = [
    { name: "Submitted", value: totalLoanStats?.totalSubmited || 0, color: PIE_COLORS[0].color, bg: PIE_COLORS[0].bg },
    { name: "Booked", value: totalLoanStats?.totalBooked || 0, color: PIE_COLORS[1].color, bg: PIE_COLORS[1].bg },
    { name: "Authorized", value: totalLoanStats?.totalAuthorized || 0, color: PIE_COLORS[2].color, bg: PIE_COLORS[2].bg },
    { name: "Initiated", value: totalLoanStats?.totalInitiated || 0, color: PIE_COLORS[3].color, bg: PIE_COLORS[3].bg },
    { name: "Paid", value: totalLoanStats?.totalPaid || 0, color: PIE_COLORS[4].color, bg: PIE_COLORS[4].bg },
    { name: "Rejected", value: totalLoanStats?.totalRejected || 0, color: PIE_COLORS[5].color, bg: PIE_COLORS[5].bg },
  ];

  const regions = [
    { name: "Greater Accra", color: "#3b82f6" },
    { name: "Ashanti", color: "#10b981" },
    { name: "Western", color: "#f59e0b" },
    { name: "Eastern", color: "#8b5cf6" },
    { name: "Central", color: "#ef4444" },
    { name: "Northern", color: "#06b6d4" },
    { name: "Volta", color: "#ec4899" },
    { name: "Upper East", color: "#f97316" },
    { name: "Upper West", color: "#14b8a6" },
    { name: "Bono", color: "#6366f1" },
    { name: "Bono East", color: "#a855f7" },
    { name: "Ahafo", color: "#84cc16" },
    { name: "Western North", color: "#d946ef" },
    { name: "Oti", color: "#0ea5e9" },
    { name: "North East", color: "#e11d48" },
    { name: "Savannah", color: "#65a30d" },
    { name: "Unknown", color: "#9ca3af" },
  ];

  useEffect(() => {
    let active = true;
    // Region stats fetch ALL members (heavy, non-paginated) — run it on its
    // own so it doesn't hold the whole dashboard behind it. The region cards
    // show their own "..." state via regionLoading until it resolves.
    fetchRegionStats();
    (async () => {
      try {
        await Promise.all([
          GetAppliedLoans(true, 1, 5, "initiated"),
          GetTotal(),
          GetTotals(),
          GetInventoryItems(),
          GetLowStock(),
          GetTotalLoanStats(),
          GetTotalTotalActiveNurse(),
          GetTotalInactiveNurse(),
          GetTotalVerifiedNurse(),
          GetTotalUnverifiedNurse(),
          GetLoansForStatistics("submitted"),
        ]);
      } catch (e) {
        console.error("Dashboard load failed", e);
      } finally {
        if (active) setInitializing(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const fetchRegionStats = async () => {
    setRegionLoading(true);
    try {
      // Fast path: backend counts by region and returns just the numbers.
      const { data: counts } = await axiosInstance.get(
        `/users/members/region-stats`
      );
      // Guard: on backends without this route the request can be matched by
      // GET /members/:staff_id and return null / a user object (200 OK). Only
      // accept a plain object whose values are all numbers; otherwise fall back.
      const isValidCounts =
        counts &&
        typeof counts === "object" &&
        !Array.isArray(counts) &&
        Object.keys(counts).length > 0 &&
        Object.values(counts).every((v) => typeof v === "number");
      if (!isValidCounts) {
        throw new Error("Invalid region-stats response shape");
      }
      const normalized = {};
      regions.forEach((r) => { normalized[r.name] = counts?.[r.name] || 0; });
      setRegionStats(normalized);
    } catch (e) {
      // Fallback for older backend without the region-stats endpoint:
      // fetch all members and count client-side (slow).
      console.warn("region-stats endpoint unavailable, falling back", e);
      try {
        const response = await axiosInstance.get(`/users/members?isPaginated=false`);
        const allMembers = response.data?.docs || response.data || [];
        const counts = {};
        regions.forEach((r) => { counts[r.name] = 0; });
        allMembers.forEach((member) => {
          const memberRegion = member?.region?.trim();
          if (memberRegion && counts.hasOwnProperty(memberRegion)) {
            counts[memberRegion]++;
          } else {
            counts["Unknown"]++;
          }
        });
        setRegionStats(counts);
      } catch (err) {
        console.error("Failed to fetch region stats", err);
      }
    } finally {
      setRegionLoading(false);
    }
  };

  useEffect(() => {
    GetLoansForStatistics(formData.status);
  }, [formData.status]);

  // Support-desk counters for the module card. Safe to fire unconditionally —
  // GetTicketStats swallows a 404 and leaves the counts at zero.
  useEffect(() => {
    GetTicketStats();
  }, []);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    }
  });

  return (
    <div className="">
      <RenderAdminPage title={"Dashboard"}>
        {initializing ? (
          <DashboardSkeleton />
        ) : (
          <>
            <div className="dashboard-home">
              <header className="dashboard-intro">
                <div>
                  {/* <p className="dashboard-eyebrow">YOUR WORKSPACE AT A GLANCE</p> */}
                  <h2>Administration overview</h2><p>Members, finances and operations.</p>
                </div>
                <Link to="/admin/report" className="dashboard-report-link"><ReceiptText size={17} /> View reports <ChevronRight size={16} /></Link>
              </header>
              <section className="dashboard-overview" aria-label="Key figures">
                <div className="dashboard-section-heading"><h3>Overview</h3><span>Membership & finances</span></div>
                {/* Stats Overview */}
                <div className="stats-grid">
                  <Link className="stat-card" to="/admin/members">
                    <div className="stat-card__icon stat-card__icon--blue">
                      <Users size={22} />
                    </div>
                    <div className="stat-card__content">
                      <p className="stat-card__label">Total Members</p>
                      <h3 className="stat-card__value">{totalMembers || 0}</h3>
                    </div>
                    <ChevronRight size={18} className="stat-card__arrow" />
                  </Link>

                  <Link className="stat-card" to="/admin/members">
                    <div className="stat-card__icon stat-card__icon--green">
                      <UserCheck size={22} />
                    </div>
                    <div className="stat-card__content">
                      <p className="stat-card__label">Active Members</p>
                      <h3 className="stat-card__value">{totalActiveMembers || 0}</h3>
                    </div>
                    <ChevronRight size={18} className="stat-card__arrow" />
                  </Link>

                  <Link className="stat-card" to="/admin/members">
                    <div className="stat-card__icon stat-card__icon--red">
                      <UserX size={22} />
                    </div>
                    <div className="stat-card__content">
                      <p className="stat-card__label">Inactive Members</p>
                      <h3 className="stat-card__value">{totalInactiveMembers || 0}</h3>
                    </div>
                    <ChevronRight size={18} className="stat-card__arrow" />
                  </Link>

                  <Link className="stat-card" to="/admin/members">
                    <div className="stat-card__icon stat-card__icon--teal">
                      <MailCheck size={22} />
                    </div>
                    <div className="stat-card__content">
                      <p className="stat-card__label">Verified Members</p>
                      <h3 className="stat-card__value">{totalVerifiedMembers || 0}</h3>
                    </div>
                    <ChevronRight size={18} className="stat-card__arrow" />
                  </Link>

                  <Link className="stat-card" to="/admin/members">
                    <div className="stat-card__icon stat-card__icon--amber">
                      <MailX size={22} />
                    </div>
                    <div className="stat-card__content">
                      <p className="stat-card__label">Unverified Members</p>
                      <h3 className="stat-card__value">{totalUnverifiedMembers || 0}</h3>
                    </div>
                    <ChevronRight size={18} className="stat-card__arrow" />
                  </Link>

                  <Link className="stat-card" to="/admin/funds">
                    <div className="stat-card__icon stat-card__icon--purple">
                      <Wallet size={22} />
                    </div>
                    <div className="stat-card__content">
                      <p className="stat-card__label">Total Funds</p>
                      <h3 className="stat-card__value"><CediSymbol /> {totalFunds || 0}</h3>
                    </div>
                    <ChevronRight size={18} className="stat-card__arrow" />
                  </Link>

                  <Link className="stat-card" to="/admin/loans-bookings">
                    <div className="stat-card__icon stat-card__icon--orange">
                      <TrendingUp size={22} />
                    </div>
                    <div className="stat-card__content">
                      <p className="stat-card__label">Initiated Loans</p>
                      <h3 className="stat-card__value">{totalLoanStats?.totalInitiated || 0}</h3>
                    </div>
                    <ChevronRight size={18} className="stat-card__arrow" />
                  </Link>
                </div>

              </section>
              <section aria-label="Workspace modules">
                <div className="dashboard-section-heading"><div><h3>Your modules</h3><p>Quick access to everyday operations</p></div><span>6 workspaces</span></div>
                {/* Module Cards */}
                <div className="module-cards">
                  <Link to={"/loans/home"} target="_blank" rel="noopener noreferrer" className="module-card module-card--blue">
                    <div className="module-card__header">
                      <div className="module-card__icon module-card__icon--blue">
                        <Banknote size={22} />
                      </div>
                      <ExternalLink size={16} className="module-card__external" />
                    </div>
                    <h3 className="module-card__title">Loans Module</h3>
                    <div className="module-card__stats">
                      <div className="module-card__stat">
                        <span className="module-card__stat-value">{totalLoanStats?.totalLoans || 0}</span>
                        <span className="module-card__stat-label">Total Loans</span>
                      </div>
                      <div className="module-card__stat">
                        <span className="module-card__stat-value">{totalLoanStats?.totalSubmited || 0}</span>
                        <span className="module-card__stat-label">New Loans</span>
                      </div>
                    </div>
                  </Link>

                  <Link to={"/mart/home"} target="_blank" rel="noopener noreferrer" className="module-card module-card--red">
                    <div className="module-card__header">
                      <div className="module-card__icon module-card__icon--red">
                        <ShoppingCart size={22} />
                      </div>
                      <ExternalLink size={16} className="module-card__external" />
                    </div>
                    <h3 className="module-card__title">Mart Module</h3>
                    <div className="module-card__stats">
                      <div className="module-card__stat">
                        <span className="module-card__stat-value">{totalOrders || 0}</span>
                        <span className="module-card__stat-label">Total Orders</span>
                      </div>
                      <div className="module-card__stat">
                        <span className="module-card__stat-value">{totalSubmitedOrders || 0}</span>
                        <span className="module-card__stat-label">New Orders</span>
                      </div>
                    </div>
                  </Link>

                  <Link to={"/admin/union"} target="_blank" rel="noopener noreferrer" className="module-card module-card--green">
                    <div className="module-card__header">
                      <div className="module-card__icon module-card__icon--green">
                        <Users size={22} />
                      </div>
                      <ExternalLink size={16} className="module-card__external" />
                    </div>
                    <h3 className="module-card__title">Unions Module</h3>
                    <div className="module-card__stats">
                      <div className="module-card__stat">
                        <span className="module-card__stat-label">Manage union operations</span>
                      </div>
                    </div>
                  </Link>

                  <Link to={"/inventory/home"} target="_blank" rel="noopener noreferrer" className="module-card module-card--yellow">
                    <div className="module-card__header">
                      <div className="module-card__icon module-card__icon--yellow">
                        <Wallet size={22} />
                      </div>
                      <ExternalLink size={16} className="module-card__external" />
                    </div>
                    <h3 className="module-card__title">Inventory Module</h3>
                    <div className="module-card__stats">
                      <div className="module-card__stat">
                        <span className="module-card__stat-value">{inventoryItemsCount}</span>
                        <span className="module-card__stat-label">Tracked Items</span>
                      </div>
                      <div className="module-card__stat">
                        <span className="module-card__stat-value">{inventoryLowCount}</span>
                        <span className="module-card__stat-label">Low Stock</span>
                      </div>
                    </div>
                  </Link>

                  <Link to={"/support/home"} target="_blank" rel="noopener noreferrer" className="module-card module-card--purple">
                    <div className="module-card__header">
                      <div className="module-card__icon module-card__icon--purple">
                        <Headset size={22} />
                      </div>
                      <ExternalLink size={16} className="module-card__external" />
                    </div>
                    <h3 className="module-card__title">Support Desk</h3>
                    <div className="module-card__stats">
                      <div className="module-card__stat">
                        <span className="module-card__stat-value">{openTickets}</span>
                        <span className="module-card__stat-label">Open Tickets</span>
                      </div>
                      {/* SLA Breached — hidden for now. Commented out whole rather
                  than just the label so the count isn't left unlabelled.
                  `breachedTickets` is still computed above; uncomment to
                  restore. */}
                      {/* <div className="module-card__stat">
                <span className="module-card__stat-value">{breachedTickets}</span>
                <span className="module-card__stat-label">SLA Breached</span>
              </div> */}
                    </div>
                  </Link>

                  {/* Stays inside the admin shell (no target=_blank) — it's an admin
              page, not a separate module. */}
                  <Link to={"/admin/statement"} className="module-card module-card--green">
                    <div className="module-card__header">
                      <div className="module-card__icon module-card__icon--green">
                        <ReceiptText size={22} />
                      </div>
                      <ChevronRight size={16} className="module-card__external" />
                    </div>
                    <h3 className="module-card__title">Member Statement</h3>
                    <div className="module-card__stats">
                      <div className="module-card__stat">
                        <span className="module-card__stat-label">
                          Search a member and download their fund statement
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>

              </section>
              {/* Loan Statistics Section */}
              <section className="chart-section">
                <div className="chart-section__header">
                  <h3 className="chart-section__title">
                    <PieIcon size={18} style={{ display: "inline", marginRight: 8, verticalAlign: "middle", color: "#6366f1" }} />
                    Loan Statistics
                  </h3>
                  <div className="chart-section__controls">
                    <label htmlFor="dashboard-loan-status" className="chart-section__filter-label">Monthly trend</label>
                    <select
                      id="dashboard-loan-status" className="chart-section__filter"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value={"submitted"}>Submitted</option>
                      <option value={"initiated"}>Initiated</option>
                      <option value={"booked"}>Booked</option>
                      <option value={"authorized"}>Authorized</option>
                      <option value={"paid"}>Paid</option>
                      <option value={"rejected"}>Rejected</option>
                    </select>
                  </div>
                </div>

                <div className="loan-stats-split">
                  {/* Loan status distribution */}
                  <div className="loan-stats-pie-panel">
                    <p className="pie-panel-title">Status Distribution</p>
                    <div>
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie
                            activeIndex={pieActiveIndex}
                            activeShape={renderActiveLoanStatus}
                            data={pieData}
                            cx="50%" cy="50%"
                            innerRadius={62} outerRadius={98}
                            paddingAngle={3}
                            dataKey="value"
                            onMouseEnter={(_, index) => setPieActiveIndex(index)}
                            startAngle={90} endAngle={-270}
                            stroke="none"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="pie-legend">
                      {pieData.map((entry, index) => (
                        <div
                          key={index}
                          className={`pie-legend-item ${pieActiveIndex === index ? "pie-legend-item--active" : ""}`}
                          onMouseEnter={() => setPieActiveIndex(index)}
                          style={{ borderColor: pieActiveIndex === index ? entry.color + "40" : "transparent" }}
                        >
                          <span className="pie-legend-dot" style={{ background: entry.color }} />
                          <span className="pie-legend-name">{entry.name}</span>
                          <span className="pie-legend-value" style={{ color: entry.color }}>{entry.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pie-total-badge">
                      <span className="pie-total-label">Total Loans</span>
                      <span className="pie-total-value">{(totalLoanStats?.totalLoans || 0).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Monthly Bar Chart Panel */}
                  <div className="loan-stats-bar-panel">
                    <p className="bar-panel-title">Monthly Trend — <span style={{ textTransform: "capitalize" }}>{formData.status}</span></p>
                    <div style={{ overflowX: "auto" }}>
                      <DashboardTrend loansStatistics={loansStatistics} />
                    </div>
                  </div>
                </div>
              </section>

              {/* Member Statistics by Region */}
              <section className="region-stats-section">
                <div className="region-stats__header">
                  <h3 className="region-stats__title">
                    <MapPin size={20} className="region-stats__title-icon" />
                    Member Statistics by Region
                  </h3>
                </div>
                <div className="region-stats-grid">
                  {regions.map((region) => (
                    <div key={region.name} className="region-stat-card">
                      <div
                        className="region-stat-card__indicator"
                        style={{ background: region.color }}
                      />
                      <div className="region-stat-card__content">
                        <p className="region-stat-card__name">{region.name}</p>
                        <h4 className="region-stat-card__value">
                          {regionLoading ? "..." : (regionStats[region.name] ?? 0)}
                        </h4>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </>
        )}

        <ToastContainer autoClose={3000} />
      </RenderAdminPage>
    </div>
  );
}

export default AdminDashboard;
